import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FluidContainer from '../../layouts/components/fluid-container';
import Skeleton from '../../skeleton';
import Spacer from '../../spacer';
import { Body, Heading, Helper } from '../../typography';
import { textPoints } from '../default-start-page';
import { getSectionsTotalTime } from '../../../lib/status';
import { sectionPropType } from '../../../lib/prop-types';

const SectionsGrid = styled.div`
    margin: calc(var(--space-unit) * 3) 0;

    display: grid;
    grid-template-columns: auto auto auto;
`;

const FirstColGrid = styled.div`
    font-weight: var(--font-sb);
    padding: var(--space-unit) calc(var(--space-unit) * 2);
    border-bottom: var(--border-size-small) solid var(--gray200);

    display: grid;
    grid-template-columns: calc(var(--space-unit) * 2) auto;
    gap: calc(var(--space-unit) / 2);
`;

const AlignRightGrid = styled.div`
    flex-direction: row-reverse;
    border-bottom: var(--border-size-small) solid var(--gray200);
    text-align: right;

    padding: var(--space-unit) calc(var(--space-unit) * 2);
`;

const NoBorderGrid = styled.div`
    font-weight: var(--font-sb);
    text-align: right;

    padding: var(--space-unit) calc(var(--space-unit) * 2);
`;

const SectionsRows = ({ sections }) =>
    sections.map((section, idx) => (
        <Fragment key={section.name}>
            <FirstColGrid>
                <div>
                    {idx + 1}
                    {'.'}
                </div>
                <div>{section.name}</div>
            </FirstColGrid>
            <AlignRightGrid>
                {section.exercisesCount}
                {' quiz'}
            </AlignRightGrid>
            <AlignRightGrid>
                {section.time / 60}
                {' minuti'}
            </AlignRightGrid>
        </Fragment>
    ));

const SectionsTotal = ({ sections }) => {
    const sumExercisesCount = sections.reduce((partialExercisesCount, section) => partialExercisesCount + section.exercisesCount, 0);

    return (
        <>
            <NoBorderGrid />
            <NoBorderGrid>
                {sumExercisesCount}
                {' quiz'}
            </NoBorderGrid>
            <NoBorderGrid>
                {getSectionsTotalTime(sections) / 60}
                {' minuti'}
            </NoBorderGrid>
        </>
    );
};

const SectionsStartPage = ({ isLoading, sections, scoreModifier }) => {
    const skeletonItems = [
        { width: 40 },
        { width: 0, height: 'small' },
        { width: 100 },
        { width: 100 },
        { width: 60 },
        { width: 0 },
        { width: 40, height: 'regular' },
        { width: 100, height: 'regular' },
    ];

    return (
        <FluidContainer flexDirection="column" maxWidth={['100%', '75%', '75%']} paddingTop={[178, 168, 140]}>
            <Skeleton isLoading={isLoading} items={skeletonItems}>
                <Body lv={4} weight="semi-bold">
                    {'PRIMA DI INIZIARE'}
                </Body>

                <Spacer size="small" />

                <Body>
                    {'Stai per cominciare una prova. Ricorda:'}
                    <ul>
                        {!!sections && (
                            <li>
                                {`la prova è divisa in ${sections?.length} sezioni`}
                                <SectionsGrid>
                                    <SectionsRows sections={sections} />
                                    <SectionsTotal sections={sections} />
                                </SectionsGrid>
                            </li>
                        )}
                        <li>{'ciascuna sezione ha un tempo massimo di svolgimento, indicato in alto a destra;'}</li>
                        <li>{'il conto alla rovescia parte appena inizi una sezione;'}</li>
                        <li>{'scaduto il tempo, vieni portato automaticamente alla sezione successiva;'}</li>
                        <li>{'puoi terminare una sezione e passare alla successiva anche prima dello scadere del tempo;'}</li>
                        <li>{'non puoi mettere in pausa la prova.'}</li>
                    </ul>

                    <Spacer size="small" />
                    <Body>
                        <strong>{'Come si calcola il punteggio?'}</strong>
                        <Spacer size="base" />
                        {`Una risposta corretta vale +${textPoints(scoreModifier?.correct)}.`}
                        {`Una risposta sbagliata vale ${textPoints(scoreModifier?.wrong)}.`}
                        {`Una risposta non data vale ${textPoints(scoreModifier?.missing)}.`}
                    </Body>
                </Body>
                <FluidContainer marginTop={[24, 24, 48]}>
                    <Heading level={2} weight="light">
                        {'Iniziamo?'}
                    </Heading>
                </FluidContainer>
                <Helper weight="semi-bold">{'Usa il pulsante "Inizia"'}</Helper>
            </Skeleton>
        </FluidContainer>
    );
};

SectionsRows.propTypes = {
    sections: PropTypes.arrayOf(sectionPropType),
};

SectionsTotal.propTypes = {
    sections: PropTypes.arrayOf(sectionPropType),
};

SectionsStartPage.propTypes = {
    isLoading: PropTypes.bool,
    sections: PropTypes.arrayOf(sectionPropType),
    scoreModifier: PropTypes.shape({
        correct: PropTypes.number,
        wrong: PropTypes.number,
        missing: PropTypes.number,
    }),
};

export default SectionsStartPage;
